import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Settings, Lightbulb } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: BarChart3,
      title: 'Strategy',
      tagline: 'Sharper strategy, built faster with better data',
      description:
        'Strategic clarity in a complex world. We help you see through the noise, identify what matters most, and build a path forward that your team can actually execute.',
      features: [
        'Market and competitive analysis',
        'Growth strategy development',
        'Data-driven decision frameworks',
        'Strategic planning workshops',
        'AI-enhanced scenario modeling',
      ],
    },
    {
      icon: Settings,
      title: 'Operations',
      tagline: 'More efficient operations with fewer bottlenecks',
      description:
        'Execution is where strategy meets reality. We help you streamline operations, remove friction, and build systems that deliver consistent results.',
      features: [
        'Process optimization',
        'Operational efficiency audits',
        'Performance improvement programs',
        'Supply chain and logistics',
        'Technology integration strategy',
      ],
    },
    {
      icon: Lightbulb,
      title: 'Advisory',
      tagline: 'Clear guidance backed by experience and modern tools',
      description:
        'Trusted counsel when the stakes are high. We provide direct, honest assessments and practical recommendations that help you move forward with confidence.',
      features: [
        'Executive advisory',
        'Board and leadership support',
        'Due diligence and assessment',
        'Transformation guidance',
        'AI readiness evaluation',
      ],
    },
  ];

  return (
    <div data-testid="services-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="container-main">
          <div className="max-w-4xl">
            <p className="label-text mb-6 animate-fade-in">Our Services</p>
            <h1 className="heading-1 mb-8 animate-slide-up">
              Better decisions.
              <br />
              Faster execution.
            </h1>
            <p className="body-text text-lg max-w-2xl animate-slide-up stagger-2">
              We combine decades of real-world operating experience with modern AI
              tools to help organizations solve problems, move faster, and get
              better results.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="pb-20 sm:pb-28">
        <div className="container-main">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-detail-${index}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-16 sm:py-24 ${
                index !== services.length - 1 ? 'border-b border-gray-200' : ''
              }`}
            >
              {/* Service Info - Alternating layout */}
              <div
                className={`lg:col-span-6 ${
                  index % 2 === 1 ? 'lg:order-2' : ''
                }`}
              >
                <div className="sticky top-32">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-slate-900 flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="label-text">0{index + 1}</span>
                  </div>
                  <h2 className="heading-2 mb-4">{service.title}</h2>
                  <p className="text-xl text-slate-900 font-medium mb-6">
                    {service.tagline}
                  </p>
                  <p className="body-text mb-8">{service.description}</p>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${index}`}
                    className="btn-primary inline-flex items-center gap-3"
                  >
                    Discuss This Service
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              {/* Features Card */}
              <div
                className={`lg:col-span-6 ${
                  index % 2 === 1 ? 'lg:order-1' : ''
                }`}
              >
                <div className="bg-gray-50 p-8 sm:p-12 h-full">
                  <h3 className="heading-3 mb-8">What We Deliver</h3>
                  <ul className="space-y-4">
                    {service.features.map((feature, fIndex) => (
                      <li
                        key={fIndex}
                        className="flex items-start gap-4"
                        data-testid={`service-${index}-feature-${fIndex}`}
                      >
                        <div className="w-2 h-2 bg-slate-900 mt-2 flex-shrink-0" />
                        <span className="body-text">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Positioning Section */}
      <section data-testid="ai-positioning" className="section-spacing bg-gray-50">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <p className="label-text mb-6">Our Approach to AI</p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              AI enhances our work.
              <br />
              <span className="text-gray-400">It doesn't replace judgment.</span>
            </h2>
            <p className="body-text text-lg max-w-2xl mx-auto">
              We use AI as a tool to analyze faster, identify patterns more clearly,
              and help our clients make better decisions. But every recommendation
              is grounded in real-world experience and sound judgment.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
